var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Euler, Matrix4, Quaternion } from 'three';
import { Cartographic, Ellipsoid, } from '@skand/three-js-renderer/bin/private/math';
import proj4 from 'proj4';
import Papa from 'papaparse';
class PegasusLoader {
    constructor() {
        this.missingData = [];
    }
    /**
     * Convert a position in some coordinate system to ECEF
     *
     * @param x          x-position in source coordinate system
     * @param y          y-position in source coordinate system
     * @param z          z-position in source coordinate system
     * @param projection Projection string of source coordinate system
     */
    calculatePosition(x, y, z, projection) {
        try {
            const ecefSpace = proj4(projection, 'EPSG:4326').forward({ x, y, z });
            return Ellipsoid.WGS_84.cartesianForCartographic(new Cartographic((ecefSpace.x * Math.PI) / 180, (ecefSpace.y * Math.PI) / 180, ecefSpace.z));
        }
        catch (error) {
            throw new Error(`Invalid Projection Error: ${error}`);
        }
    }
    /**
     * Calculate the rotation of the panorama in ECEF
     *
     * @param x          x-position in source coordinate system
     * @param y          y-position in source coordinate system
     * @param z          z-position in source coordinate system
     * @param omega      Omega angle
     * @param phi        Phi angle
     * @param kappa      Kappa angle
     * @param projection Projection string of source coordinate system
     */
    calculateRotation(x, y, z, omega, phi, kappa, projection) {
        const eps = 0.001;
        const origin = this.calculatePosition(x, y, z, projection);
        const xOffset = this.calculatePosition(x + eps, y, z, projection);
        const yOffset = this.calculatePosition(x, y + eps, z, projection);
        // Calculate rotation axes
        const xAxis = xOffset.clone().sub(origin).normalize();
        const yAxis = yOffset.clone().sub(origin).normalize();
        const zAxis = xAxis.clone().cross(yAxis).normalize();
        xAxis.copy(yAxis).cross(zAxis).normalize();
        // Create final rotation matrix
        const basis = new Matrix4().makeBasis(xAxis, yAxis, zAxis);
        const localRotation = new Matrix4().makeRotationFromEuler(new Euler((omega * Math.PI) / 200, (phi * Math.PI) / 200, (kappa * Math.PI) / 200, 'XYZ'));
        const correction = new Matrix4().makeRotationY(-Math.PI / 2);
        basis.multiply(localRotation).multiply(correction);
        return new Quaternion().setFromRotationMatrix(basis);
    }
    /**
     * Get the list of photos (filenames) that have missing data
     */
    getPhotosMissingData() {
        return this.missingData;
    }
    /**
     * Get the list of panoramic objects from an MLS CSV input string
     *
     * @param csv        CSV string
     * @param projection Projection string of source coordinate system
     */
    read(csv, projection) {
        return __awaiter(this, void 0, void 0, function* () {
            const panoramas = [];
            const parsed = Papa.parse(csv, {
                header: true,
                delimitersToGuess: ['; ', ', ', ';', ','],
            });
            for (const entry of parsed.data) {
                const values = Object.values(entry);
                // Parse individual column values
                const fileName = values[0];
                const x1 = parseFloat(values[2]);
                const y1 = parseFloat(values[3]);
                const z1 = parseFloat(values[4]);
                const omega = parseFloat(values[10]);
                const phi = parseFloat(values[11]);
                const kappa = parseFloat(values[12]);
                // Skip invalid data
                if (!fileName)
                    continue;
                if (isNaN(x1) ||
                    isNaN(y1) ||
                    isNaN(z1) ||
                    isNaN(omega) ||
                    isNaN(phi) ||
                    isNaN(kappa)) {
                    this.missingData.push(fileName);
                    continue;
                }
                const position = this.calculatePosition(x1, y1, z1, projection);
                const rotation = this.calculateRotation(x1, y1, z1, omega, phi, kappa, projection);
                panoramas.push({
                    fileName,
                    position,
                    rotation,
                });
            }
            return panoramas;
        });
    }
}
export default PegasusLoader;
